/**
 * 底池与边池：未跟注退还 → 分层切池 → 逐池比牌分配。见 docs/ARCHITECTURE.md §4.8 / §4.10。
 *
 * 这是全项目筹码守恒的唯一出口，三个硬不变式全压在这个文件上（红线 #19）：
 *
 *   Σ contributed == Σ won == potTotal
 *   endingStack == startingStack - contributed + won
 *   Σ endingStack == Σ startingStack
 *
 * 为什么「退还」必须先于「切池」：退还的钱从来就不属于底池——它是没有任何人
 * 匹配过的下注。若先切池再退，主池金额会先虚高再回落（run-it-out 期间 UI 会出现
 * 底池突然变小的鬼畜），更糟的是 `contributed` 口径会被污染：单挑翻前加注到 100
 * 对手弃牌，旧口径算出 net=+100 而真实盈亏只有 +10，BB/100 与净筹码曲线被系统性
 * 放大。所以本模块的调用顺序是不可调换的（§4.10 的反例）。
 *
 * 铁律 #1：除 `settleUncalledBet` 明确就地退还 draft 之外，全部是纯函数。
 */
import type { Chips, SeatIndex } from '../ids.js'
import { compareRanks } from './evaluator.js'
import { InvariantError } from './types.js'
import type { HandRank, PlayerHandStatus, Pot, PotAward } from './types.js'

// ============================================================================
// 输入形状
// ============================================================================

/**
 * 座位集合的两种写法都接受：`HandState.players` 是 `Record<SeatIndex, HandPlayer>`，
 * 而测试与 AI 模拟更顺手的是数组。统一在内部归一化，省掉每个调用点一次 `Object.values`。
 */
export type PlayerCollection<T> = readonly T[] | Readonly<Record<SeatIndex, T>>

/** `buildPots` 需要的最小视图；`HandPlayer` 天然满足（结构化类型） */
export interface PotContributor {
  readonly seat: SeatIndex
  readonly committedThisHand: Chips
  readonly status: PlayerHandStatus
  /**
   * `committedThisHand` 中属于**死钱**的部分，缺省 0。目前唯一的来源是
   * `anteMode: 'bigBlindAnte'`——大盲替全桌缴的那笔 ante。
   *
   * 为什么死钱必须单独标出来：分层切池的前提是「投得多的人，多出来的那部分只有
   * 同样投到这个档位的人才有资格争」。这对下注成立，对大盲替全桌缴的 ante 不成立——
   * 那笔钱没有任何对手需要匹配，它一进池就属于本手的赢家。若照常分层，大盲的
   * `committedThisHand` 恒比别人高出一个 ante，ante 就会每手被切成「只有大盲有资格」
   * 的私人边池原路返回，`bigBlindAnte` 这个模式直接失效。
   *
   * `each` 模式的 ante **不是**死钱：人人各缴一份，筹码不足者只能缴一部分，
   * 他也就只配赢到那个档位——照常分层才是对的（§4.13）。
   */
  readonly deadMoney?: Chips
}

/** `settleUncalledBet` 需要就地改写的两个字段；`HandPlayer` 同样天然满足 */
export interface UncalledBetPlayer {
  readonly seat: SeatIndex
  stack: Chips
  committedThisHand: Chips
}

/** 与 `HandState.uncalledReturn` / `HandResult.uncalledReturn` 同形 */
export interface UncalledReturn {
  readonly seat: SeatIndex
  readonly amount: Chips
}

/** 逐座位牌力表。`Map` 与普通对象都接受，engine 用哪种都行 */
export type RanksBySeat =
  | ReadonlyMap<SeatIndex, HandRank>
  | Readonly<Record<SeatIndex, HandRank>>

export interface BuildPotsOptions {
  /**
   * 只在「某一层没有任何 eligible 座位」这种理论不可达的分支上被调用。
   * 模糊测试靠它统计命中次数——长期为 0 才能证明合法动作序列真的到不了这里。
   */
  readonly onInvariantWarning?: (message: string) => void
}

function toList<T>(players: PlayerCollection<T>): readonly T[] {
  if (Array.isArray(players)) return players as readonly T[]
  // Record<number, T> 不满足 Object.values 要求的 string 索引签名，只能借道 unknown
  return Object.values(players as unknown as Record<string, T>)
}

function assertChips(value: number, what: string): void {
  // 红线 #17：全系统禁用浮点筹码，最小单位 1
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new InvariantError(`${what} must be a non-negative safe integer, got ${String(value)}`)
  }
}

// ============================================================================
// 步骤一：未跟注下注退还（必须先于切池）
// ============================================================================

/**
 * 算出本手应退还给谁多少，**不做任何改写**。
 *
 * 用「最高投入 − 次高投入」而不是「追踪最后一次下注」：该式对任何弃牌组合都成立，
 * 因为弃牌者的 `committedThisHand` 恰好是他实际**已匹配**的金额，天然构成正确的次高水位。
 * 追踪最后一次下注则要额外区分「加注被短码全下部分跟注」等一堆情形。
 */
export function computeUncalledBet(
  players: PlayerCollection<UncalledBetPlayer>,
): UncalledReturn | null {
  const list = toList(players)
  if (list.length < 2) return null

  // 只需要前两名，排序是 O(n log n) 但 n ≤ 9，不值得为此手写一遍扫描
  let top: UncalledBetPlayer | undefined
  let second: UncalledBetPlayer | undefined
  for (const p of list) {
    assertChips(p.committedThisHand, `player ${String(p.seat)} committedThisHand`)
    if (top === undefined || p.committedThisHand > top.committedThisHand) {
      second = top
      top = p
    } else if (second === undefined || p.committedThisHand > second.committedThisHand) {
      second = p
    }
  }
  if (top === undefined || second === undefined) return null

  const excess = top.committedThisHand - second.committedThisHand
  if (excess <= 0) return null
  return { seat: top.seat, amount: excess }
}

/**
 * 把未被跟注的部分退还给投入最高的玩家，**就地改写传入的 player 对象**。
 *
 * ⚠️ 唯一的非纯导出。铁律 #3 允许对 draft 副本原地修改——调用方必须传自己持有的
 * draft（engine 的 street-end 流程正是如此），绝不能传共享的 state 子结构。
 * 只想知道「会退多少」而不落账，用 `computeUncalledBet`。
 *
 * 调用时机（§4.8）：每条街下注轮结束时、`buildPots` 之前。
 * `payout` 阶段保留幂等兜底 `if (state.uncalledReturn === null) settleUncalledBet(...)`；
 * 因为一手牌中至多触发一次非零退还（产生未跟注余额意味着除该玩家外所有人已 folded
 * 或 allIn，此后不再有下注轮），兜底调用不会重复退钱。
 */
export function settleUncalledBet(
  players: PlayerCollection<UncalledBetPlayer>,
): UncalledReturn | null {
  const ret = computeUncalledBet(players)
  if (ret === null) return null

  const top = toList(players).find((p) => p.seat === ret.seat)
  if (top === undefined) {
    throw new InvariantError(`settleUncalledBet: seat ${String(ret.seat)} vanished from collection`)
  }
  // 退还既加回筹码也冲减 committedThisHand——后者是 §4.10 口径的关键：
  // contributed 不含退还，否则 net 会被放大
  top.stack += ret.amount
  top.committedThisHand -= ret.amount
  return ret
}

// ============================================================================
// 步骤二：分层切池
// ============================================================================

interface PotLayer {
  amount: Chips
  eligibleSeats: SeatIndex[]
  capLevel: Chips
}

function sameSeats(a: readonly SeatIndex[], b: readonly SeatIndex[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/**
 * 按投入档位分层切池。**必须在 `settleUncalledBet` 之后调用**，否则未跟注的钱会进池。
 *
 * 成立性：`levels` 恰是各玩家投入额的去重集合，因此 `committedThisHand > prev`
 * 蕴含 `committedThisHand >= L`，每个贡献者在该层恰好贡献 `L - prev`，**无需 min() 钳位**。
 * ante 作为最低一档天然参与分层（筹码少于 ante 的玩家自成一层）。
 *
 * 弃牌者的钱留在池里但不进 `eligibleSeats`——这正是「输掉的钱归赢家」的实现方式。
 *
 * `deadMoney`（bigBlindAnte）不参与分层，整笔并进最低一档——理由见 `PotContributor`。
 */
export function buildPots(
  players: PlayerCollection<PotContributor>,
  options?: BuildPotsOptions,
): Pot[] {
  const list = toList(players)
  const wagered = new Map<SeatIndex, Chips>()
  let totalDead = 0
  for (const p of list) {
    assertChips(p.committedThisHand, `player ${String(p.seat)} committedThisHand`)
    const dead = p.deadMoney ?? 0
    assertChips(dead, `player ${String(p.seat)} deadMoney`)
    if (dead > p.committedThisHand) {
      throw new InvariantError(
        `player ${String(p.seat)} deadMoney ${String(dead)} > committedThisHand ${String(p.committedThisHand)}`,
      )
    }
    wagered.set(p.seat, p.committedThisHand - dead)
    totalDead += dead
  }
  const wageredOf = (p: PotContributor): Chips => wagered.get(p.seat) ?? 0

  // 分层只看**下注**部分：死钱没有对手需要匹配，不构成档位
  const levels = [...new Set(list.map(wageredOf).filter((c) => c > 0))].sort((a, b) => a - b)

  const layers: PotLayer[] = []
  let prev = 0
  for (const level of levels) {
    const contributors = list.filter((p) => wageredOf(p) > prev)
    const amount = (level - prev) * contributors.length
    if (amount > 0) {
      layers.push({
        amount,
        eligibleSeats: contributors
          .filter((p) => p.status !== 'folded')
          .map((p) => p.seat)
          .sort((a, b) => a - b),
        capLevel: level,
      })
    }
    prev = level
  }

  if (totalDead > 0) {
    /**
     * 死钱单独成最底一层（capLevel 0）：全体未弃牌的参与者都有资格赢它，包括「只缴了死钱、
     * 一分钱没下注」的短码（大盲筹码不够一个 ante、补盲者筹码只够死小盲）——他人在牌局里，
     * 就不能一无所得。
     *
     * ★ 但他**只能**赢这一层：以前死钱是并进最低下注层的，只交了 5 个死钱就全下的人，
     *   能赢走其余三人各 10 的下注（投 5 赢 35），违反「全下的人只能赢到自己匹配的那部分」。
     *   没有这种人时，这一层的 eligible 与最低下注层完全相同，mergeLayers 会把两层合并，
     *   结果与以前逐字节一致。死钱层的 eligible 是最低下注层的超集，
     *   「eligible 随档位单调收缩」依然成立。
     */
    const inHand = list
      .filter((p) => p.status !== 'folded' && p.committedThisHand > 0)
      .map((p) => p.seat)
      .sort((a, b) => a - b)
    layers.unshift({ amount: totalDead, eligibleSeats: inHand, capLevel: 0 })
  }
  if (layers.length === 0) return []

  return mergeLayers(layers, options)
}

/**
 * 两件事合在一遍扫描里做：
 * 1. 防御性兜底——把「没有任何 eligible 座位」的层并进下方最近一个有 eligible 的池。
 *    合法动作序列下不可达；一旦发生，把钱塞进下方池至少还守恒，静默丢弃则永远查不出来。
 * 2. 合并相邻 eligible 完全相同的层——纯显示优化，不改变分配结果。
 *    因为 level 递增 ⟹ contributors 单调收缩 ⟹ eligible 单调收缩，相同集合必然相邻。
 */
function mergeLayers(layers: readonly PotLayer[], options?: BuildPotsOptions): Pot[] {
  const warn = options?.onInvariantWarning
  const kept: PotLayer[] = []
  let orphaned = 0 // 完全找不到归宿的金额（只在「所有层都没有 eligible」时才非零）

  for (const layer of layers) {
    if (layer.eligibleSeats.length === 0) {
      // eligible 随 level 递增单调收缩，所以空层必然是**后缀**：要么下方已有池可以接住，
      // 要么整手牌一个有资格的人都没有
      warn?.(`buildPots: pot layer at capLevel ${String(layer.capLevel)} has no eligible seats`)
      const below = kept[kept.length - 1]
      if (below === undefined) orphaned += layer.amount
      // 并入下方池时**不**抬高 capLevel：capLevel 描述的是「eligible 座位投够了多少」，
      // 而这些座位并没有投到更高的档位
      else below.amount += layer.amount
      continue
    }
    const last = kept[kept.length - 1]
    if (last !== undefined && sameSeats(last.eligibleSeats, layer.eligibleSeats)) {
      last.amount += layer.amount
      last.capLevel = layer.capLevel
    } else {
      kept.push({ ...layer })
    }
  }

  if (orphaned > 0) {
    // 有筹码却没有任何人有资格赢：只可能是引擎把最后一个未弃牌玩家也标成了 folded。
    // 铁律 #4——外部输入到不了这里，走到这里就是引擎自身坏了。按比例退还无法用 Pot[]
    // 表达，而静默返回空数组会直接吞掉筹码（违反红线 #19），所以这里必须炸。
    throw new InvariantError(
      `buildPots: ${String(orphaned)} chips have no eligible winner (every contributor folded)`,
    )
  }

  return kept.map((layer, index) => ({
    index,
    amount: layer.amount,
    eligibleSeats: layer.eligibleSeats,
    capLevel: layer.capLevel,
  }))
}

/** `state.potTotal = Σ pots.amount`（退还之后的口径） */
export function potTotal(pots: readonly Pot[]): Chips {
  let sum = 0
  for (const pot of pots) sum += pot.amount
  return sum
}

// ============================================================================
// 步骤三：逐池比牌与分配
// ============================================================================

/**
 * 从 `startSeat` 开始顺时针枚举全部 `maxSeats` 个座位号。
 * `startSeat` 允许越界（调用方普遍写 `buttonSeat + 1`），内部取模归一。
 */
export function seatsClockwiseFrom(startSeat: SeatIndex, maxSeats: number): SeatIndex[] {
  if (!Number.isSafeInteger(maxSeats) || maxSeats < 1) {
    throw new InvariantError(
      `seatsClockwiseFrom: maxSeats must be a positive integer, got ${String(maxSeats)}`,
    )
  }
  if (!Number.isSafeInteger(startSeat)) {
    throw new InvariantError(`seatsClockwiseFrom: startSeat must be an integer, got ${String(startSeat)}`)
  }
  const start = ((startSeat % maxSeats) + maxSeats) % maxSeats
  const out: SeatIndex[] = []
  for (let i = 0; i < maxSeats; i++) out.push((start + i) % maxSeats)
  return out
}

function lookupRank(ranks: RanksBySeat, seat: SeatIndex): HandRank | undefined {
  if (ranks instanceof Map) return (ranks as ReadonlyMap<SeatIndex, HandRank>).get(seat)
  return (ranks as Readonly<Record<SeatIndex, HandRank | undefined>>)[seat]
}

/**
 * 逐池比牌分配。**只依赖 `ranksBySeat`，与谁亮不亮牌无关**——所以 awards 必须先于
 * `computeShowdown` 算出（§4.11 注 1）：一个玩家可能输掉主池却赢下某个边池，
 * 「单调扫描 muck」在多边池下会漏掉他。
 *
 * 奇数筹码（红线 #24）：余数从**庄家按钮左手第一位** `(button+1) mod maxSeats` 开始
 * 顺时针逐 1 枚发，**每个边池独立取余，不跨池累积**。单挑时按钮即小盲，故余数给非按钮
 * 玩家。绝不按 seat 数值大小排序——按钮不在 0 号位时那是错的。
 *
 * 只有一个 eligible 座位的池跳过比牌，`handRank` 置 null（未经比拼获胜）；
 * 全员弃牌只剩一人时全部池都落进这一分支，天然满足「赢家永不亮牌」（红线 #3）。
 */
export function distributePots(
  pots: readonly Pot[],
  ranksBySeat: RanksBySeat,
  buttonSeat: SeatIndex,
  maxSeats: number,
): PotAward[] {
  const clockwise = seatsClockwiseFrom(buttonSeat + 1, maxSeats)
  const awards: PotAward[] = []

  for (const pot of pots) {
    assertChips(pot.amount, `pot ${String(pot.index)} amount`)
    if (pot.eligibleSeats.length === 0) {
      throw new InvariantError(`distributePots: pot ${String(pot.index)} has no eligible seats`)
    }

    const only = pot.eligibleSeats[0]
    if (pot.eligibleSeats.length === 1 && only !== undefined) {
      awards.push({ potIndex: pot.index, seat: only, amount: pot.amount, handRank: null, oddChip: 0 })
      continue
    }

    let best: HandRank | undefined
    for (const seat of pot.eligibleSeats) {
      const rank = lookupRank(ranksBySeat, seat)
      if (rank === undefined) {
        throw new InvariantError(
          `distributePots: missing rank for eligible seat ${String(seat)} in pot ${String(pot.index)}`,
        )
      }
      if (best === undefined || compareRanks(rank.value, best.value) > 0) best = rank
    }
    if (best === undefined) throw new InvariantError('distributePots: unreachable empty winner search')
    const bestValue = best.value

    // 先按顺时针顺序筛 winners，而不是「先筛 winners 再排序」——后者极易退化成按 seat 排序
    const winners = clockwise.filter((seat) => {
      if (!pot.eligibleSeats.includes(seat)) return false
      const rank = lookupRank(ranksBySeat, seat)
      return rank !== undefined && compareRanks(rank.value, bestValue) === 0
    })
    if (winners.length === 0) {
      // eligible 座位号落在 [0, maxSeats) 之外时才可能发生，属于 engine 传参坏了
      throw new InvariantError(
        `distributePots: pot ${String(pot.index)} winners not covered by maxSeats=${String(maxSeats)}`,
      )
    }

    const share = Math.floor(pot.amount / winners.length)
    const remainder = pot.amount % winners.length
    for (let i = 0; i < winners.length; i++) {
      const seat = winners[i]!
      const oddChip = i < remainder ? 1 : 0
      awards.push({
        potIndex: pot.index,
        seat,
        amount: share + oddChip,
        handRank: lookupRank(ranksBySeat, seat) ?? null,
        oddChip,
      })
    }
  }

  return awards
}

/**
 * `won` 汇总（§4.10）：**不含退还**。没赢到任何池的座位不出现在结果里，
 * 由调用方按需补 0（`HandResult.wonBySeat` 要求全座位齐全）。
 */
export function wonBySeat(awards: readonly PotAward[]): Record<SeatIndex, Chips> {
  const out: Record<SeatIndex, Chips> = {}
  for (const award of awards) {
    out[award.seat] = (out[award.seat] ?? 0) + award.amount
  }
  return out
}

/** Σ awards.amount；与 `potTotal(pots)` 必须逐手相等（红线 #19） */
export function totalAwarded(awards: readonly PotAward[]): Chips {
  let sum = 0
  for (const award of awards) sum += award.amount
  return sum
}
